import { useEffect, useRef, useState } from "react";
import { Eye } from "lucide-react";

/**
 * Minimal contentEditable + execCommand WYSIWYG editor. Includes an HTML-source
 * toggle and RTL direction. Same shape as the legacy editor — keeps the saved
 * HTML byte-compatible.
 */
export function WysiwygEditor({
  value,
  onChange,
  placeholder,
  minHeight = 200,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  minHeight?: number;
}) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [showSource, setShowSource] = useState(false);

  const exec = (cmd: string, arg?: string) => {
    document.execCommand(cmd, false, arg);
    editorRef.current?.focus();
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  };

  const handleInput = () => {
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  };

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  if (showSource) {
    return (
      <div className="overflow-hidden rounded-lg border">
        <div className="flex items-center justify-between border-b bg-muted/50 px-2 py-1">
          <span className="text-xs font-medium text-muted-foreground">
            HTML Source
          </span>
          <button
            type="button"
            onClick={() => setShowSource(false)}
            className="flex items-center gap-1 rounded px-2 py-1 text-xs text-primary hover:bg-primary/10"
          >
            <Eye className="h-3 w-3" /> Visual Editor
          </button>
        </div>
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          dir="rtl"
          className="w-full bg-background p-3 font-mono text-sm focus:outline-none"
          style={{ minHeight }}
        />
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border">
      <div className="flex flex-wrap items-center gap-1 border-b bg-muted/50 px-2 py-1.5">
        <ToolbarButton onClick={() => exec("bold")} title="Bold" className="font-bold">
          B
        </ToolbarButton>
        <ToolbarButton
          onClick={() => exec("italic")}
          title="Italic"
          className="italic"
        >
          I
        </ToolbarButton>
        <ToolbarButton
          onClick={() => exec("underline")}
          title="Underline"
          className="underline"
        >
          U
        </ToolbarButton>
        <Divider />
        <ToolbarButton
          onClick={() => exec("insertUnorderedList")}
          title="Bullet List"
        >
          • List
        </ToolbarButton>
        <ToolbarButton
          onClick={() => exec("insertOrderedList")}
          title="Numbered List"
        >
          1. List
        </ToolbarButton>
        <Divider />
        <ToolbarButton
          onClick={() => {
            const url = prompt("Enter link URL:");
            if (url) exec("createLink", url);
          }}
          title="Add Link"
          className="text-blue-600"
        >
          Link
        </ToolbarButton>
        <ToolbarButton onClick={() => exec("unlink")} title="Remove Link">
          Unlink
        </ToolbarButton>
        <Divider />
        <ToolbarButton onClick={() => exec("removeFormat")} title="Clear Formatting">
          Clear
        </ToolbarButton>
        <div className="flex-1" />
        <ToolbarButton
          onClick={() => setShowSource(true)}
          title="View HTML Source"
          className="text-muted-foreground"
        >
          {"</>"}
        </ToolbarButton>
      </div>

      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onBlur={handleInput}
        dir="rtl"
        className="prose prose-sm dark:prose-invert max-w-none bg-background p-4 focus:outline-none"
        style={{ minHeight }}
        dangerouslySetInnerHTML={{ __html: value }}
        data-placeholder={placeholder}
      />
    </div>
  );
}

function ToolbarButton({
  onClick,
  title,
  children,
  className,
}: {
  onClick: () => void;
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`rounded px-2 py-1 text-xs hover:bg-muted ${className ?? ""}`}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <div className="mx-1 h-5 w-px bg-border" />;
}
